// ============================================================
// GestorRingOver.jsx — migrado al Design System v2
//
// Ubicación: src/assets/componentes/GestorRingOver.jsx
// REEMPLAZA al archivo anterior.
//
// Cambios respecto al v1:
//  - Imports apuntan a ./ui (DS v2) en vez de ./ui-v2
//  - Header sobrio sin cursivas editoriales
//  - 8 KPIs en 2 filas de 4 (Total | Contestadas | Etiquetadas | Salientes
//                            VN | RN | Duración | % Conversión)
//  - Eliminada la fila "Con etiqueta / Sin etiquetar" (integrada en KPIs)
//  - Paleta corporativa actualizada (#003c66 / #0066b3)
//  - DataManagerModal usa la API simple del Dialog v2
//  - Recharts mantenido (paleta de gráficos ajustada)
//
// SIN CAMBIOS:
//  - Toda la lógica de fetch, aggregate, chunkToStats
//  - Estados y useMemos
//  - Comparación con equipo, ranking de teleoperadoras
//  - Modal de gestión de datos (carga/borrado de meses)
// ============================================================

import React, { useState, useMemo, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, AreaChart, Area,
} from 'recharts';
import {
  Phone, PhoneOutgoing, Clock, Award, Tag, AlertTriangle, CheckCircle2,
  Database, Trash2, Calendar, Loader2, RefreshCw, Settings,
  TrendingUp,
} from 'lucide-react';

import {
  fetchRingoverChunks, deleteRingoverMonth,
  syncRingoverAhora, fetchLastImport,
} from '../../peticiones_API/PeticionesRingOver';

// DS v2 — todo desde el barrel export
import {
  Button, Select, PeriodFilter,
  Card, CardContent,
  Dialog, KpiCard, cn,
} from './ui';

import { UsuarioContext } from '../providers/personaContext';

// ============================================================
// LABELS DE PERIODO
// ============================================================
const PERIOD_LABELS = {
  'all': 'Todo el periodo',
  'month-current': 'Mes actual',
  'month-prev': 'Mes anterior',
  'last-7d': 'Últimos 7 días',
  'last-30d': 'Últimos 30 días',
  'last-90d': 'Últimos 90 días',
  'quarter-current': 'Trimestre actual',
  'year-current': 'Año actual',
};

function getPeriodLabel(period) {
  if (!period) return 'Todo el periodo';
  if (period.preset && period.preset !== 'custom') {
    return PERIOD_LABELS[period.preset] || 'Periodo';
  }
  if (period.from && period.to) return `${period.from} a ${period.to}`;
  if (period.from) return `Desde ${period.from}`;
  return 'Periodo personalizado';
}

// ============================================================
// CONSTANTS
// ============================================================
const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DOW_LIST = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];

// Paleta de gráficos (Recharts no resuelve variables CSS)
const CHART = {
  brandDark:  '#003c66',
  brandLight: '#0066b3',
  success:    '#10b981',
  danger:     '#ef4444',
  warning:    '#f59e0b',
  purple:     '#8b5cf6',
  muted:      '#94a3b8',
  ink:        '#0f172a',
  gridLine:   '#e2e8f0',
};

// ============================================================
// HELPERS
// ============================================================
const fmtN = (n) => new Intl.NumberFormat('es-ES').format(Math.round(n || 0));
const fmtDur = (s) => {
  if (!s) return '0s';
  const t = Math.round(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};
const fmtPct = (n, d) => (d === 0 ? '0%' : `${((n / d) * 100).toFixed(1)}%`);

function monthSortKey(label) {
  const parts = label.split(' ');
  const idx = MONTH_NAMES.indexOf(parts[0]);
  const year = parseInt(parts[1], 10) || 0;
  return year * 12 + idx;
}

function monthLabelToYM(label) {
  const parts = label.split(' ');
  return {
    year: parseInt(parts[1], 10),
    month: MONTH_NAMES.indexOf(parts[0]) + 1,
  };
}

function chunkToStats(c) {
  return {
    total: c.t || 0, answered: c.a || 0, missed: c.m || 0,
    inbound: c.i || 0, outbound: c.o || 0,
    totalDuration: c.td || 0,
    avgDuration: c.t > 0 ? c.td / c.t : 0,
    sinEtiqueta: c.se || 0, conEtiqueta: c.ce || 0,
    vnTotal: c.vn || 0, rnTotal: c.rn || 0,
    vnVisitaConfirmada: c.vnvc || 0, vnSeguimiento: c.vns || 0,
    vnNoLocalizable: c.vnnl || 0, vnKo: c.vnko || 0, vnVentaConfirmada: c.vnvent || 0,
    rnVisitaConfirmada: c.rnvc || 0, rnSeguimiento: c.rns || 0,
    rnNoLocalizable: c.rnnl || 0, rnKo: c.rnko || 0, rnVentaConfirmada: c.rnvent || 0,
  };
}

function emptyStats() {
  return {
    total: 0, answered: 0, missed: 0, inbound: 0, outbound: 0,
    totalDuration: 0, avgDuration: 0,
    sinEtiqueta: 0, conEtiqueta: 0, vnTotal: 0, rnTotal: 0,
    vnVisitaConfirmada: 0, vnSeguimiento: 0, vnNoLocalizable: 0, vnKo: 0, vnVentaConfirmada: 0,
    rnVisitaConfirmada: 0, rnSeguimiento: 0, rnNoLocalizable: 0, rnKo: 0, rnVentaConfirmada: 0,
  };
}

function addStats(target, source) {
  for (const k of Object.keys(source)) {
    if (k === 'avgDuration') continue;
    target[k] = (target[k] || 0) + (source[k] || 0);
  }
}

function aggregate(chunks) {
  const months = Object.keys(chunks).sort((a, b) => monthSortKey(a) - monthSortKey(b));
  const users = {};
  const userSet = new Set();

  const g = emptyStats();
  g.byMonth = {};
  g.byDay = {};
  g.byHour = Array(24).fill(0);
  g.byDayOfWeek = { Lunes: 0, Martes: 0, Miércoles: 0, Jueves: 0, Viernes: 0, Sábado: 0, Domingo: 0 };

  for (const month of months) {
    const monthAgg = emptyStats();

    for (const [userName, chunk] of Object.entries(chunks[month].users)) {
      userSet.add(userName);
      if (!users[userName]) {
        users[userName] = emptyStats();
        users[userName].byMonth = {};
        users[userName].byDay = {};
        users[userName].byHour = Array(24).fill(0);
        users[userName].byDayOfWeek = { Lunes: 0, Martes: 0, Miércoles: 0, Jueves: 0, Viernes: 0, Sábado: 0, Domingo: 0 };
      }

      const u = users[userName];
      const stats = chunkToStats(chunk);

      addStats(u, stats);
      addStats(g, stats);
      addStats(monthAgg, stats);

      const userMonthEntry = { ...stats };
      userMonthEntry.byDay = {};
      userMonthEntry.byHour = chunk.bh.slice();
      userMonthEntry.byDayOfWeek = { ...chunk.bw };
      for (const [d, dv] of Object.entries(chunk.bd)) {
        userMonthEntry.byDay[d] = {
          total: dv.t, answered: dv.a, missed: dv.m,
          sinEtiqueta: dv.se, duration: dv.d,
        };
      }
      u.byMonth[month] = userMonthEntry;

      for (const [d, dv] of Object.entries(chunk.bd)) {
        if (!u.byDay[d]) u.byDay[d] = { total: 0, answered: 0, missed: 0, sinEtiqueta: 0, duration: 0 };
        u.byDay[d].total += dv.t;
        u.byDay[d].answered += dv.a;
        u.byDay[d].missed += dv.m;
        u.byDay[d].sinEtiqueta += dv.se;
        u.byDay[d].duration += dv.d;
        if (!g.byDay[d]) g.byDay[d] = { total: 0, answered: 0, missed: 0, sinEtiqueta: 0, duration: 0 };
        g.byDay[d].total += dv.t;
        g.byDay[d].answered += dv.a;
        g.byDay[d].missed += dv.m;
        g.byDay[d].sinEtiqueta += dv.se;
        g.byDay[d].duration += dv.d;
      }
      for (let h = 0; h < 24; h++) {
        u.byHour[h] += chunk.bh[h] || 0;
        g.byHour[h] += chunk.bh[h] || 0;
      }
      for (const dow of DOW_LIST) {
        u.byDayOfWeek[dow] += chunk.bw[dow] || 0;
        g.byDayOfWeek[dow] += chunk.bw[dow] || 0;
      }
    }
    g.byMonth[month] = monthAgg;
  }

  for (const u of Object.values(users)) {
    u.avgDuration = u.total > 0 ? u.totalDuration / u.total : 0;
  }
  g.avgDuration = g.total > 0 ? g.totalDuration / g.total : 0;
  for (const m of Object.values(g.byMonth)) {
    m.avgDuration = m.total > 0 ? m.totalDuration / m.total : 0;
  }

  return {
    global: g,
    users,
    userList: [...userSet].sort((a, b) => a.localeCompare(b, 'es')),
    months,
  };
}

// ============================================================
// SUB-COMPONENTS
// ============================================================
const SectionHeader = ({ title, subtitle, icon: Icon }) => (
  <div className="flex items-start gap-2 mb-3">
    {Icon && <Icon className="size-4 text-[hsl(var(--brand-light))] mt-0.5" strokeWidth={1.75} />}
    <div>
      <div className="text-sm font-semibold text-slate-900 leading-tight">{title}</div>
      {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
  </div>
);

const TagBar = ({ label, count, total, color, sublabel }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="mb-2.5">
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-xs text-slate-700 font-medium">
          {label}
          {sublabel && <span className="text-slate-400 font-normal ml-1.5 text-[11px]">{sublabel}</span>}
        </span>
        <span className="text-xs text-slate-500">
          <span className="font-medium text-slate-900 text-[13px] tabular-nums">{fmtN(count)}</span>
          <span className="ml-1.5 text-slate-400 tabular-nums">{pct.toFixed(1)}%</span>
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${Math.min(pct, 100)}%`, background: color }}
        />
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-slate-900 text-white px-3.5 py-2.5 rounded-md text-xs shadow-lg">
      <div className="text-[13px] mb-1.5 text-white/70 font-medium">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex justify-between gap-4 items-center" style={{ marginTop: i > 0 ? 3 : 0 }}>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2 rounded-full" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="font-medium tabular-nums">{fmtN(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// DATA MANAGER MODAL (adaptado a la API simple del Dialog v2)
// ============================================================
const DataManagerModal = ({ open, onClose, chunks, lastImport, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const months = Object.keys(chunks).sort((a, b) => monthSortKey(a) - monthSortKey(b));

  const removeMonth = async (monthLabel) => {
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const { year, month } = monthLabelToYM(monthLabel);
      await deleteRingoverMonth(year, month);
      setSuccess(`Eliminado ${monthLabel}.`);
      await onChange();
    } catch (e) {
      const apiMsg = e.response?.data?.message || e.message || 'Error al borrar el mes';
      setError(apiMsg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Gestión de datos"
      description="Cargar nuevos meses · ver y eliminar los existentes"
      size="lg"
    >
      <div className="px-5 py-4 overflow-y-auto">
        {lastImport && (
          <div className="mb-4 px-3.5 py-3 rounded-md bg-blue-50">
            <div className="text-xs text-[hsl(var(--brand-light))] flex items-center gap-1.5">
              <Clock size={14} /> Última sincronización
            </div>
            <div className="text-sm text-slate-900 mt-1">
              {new Date(lastImport.importedAt).toLocaleString('es-ES')} · {lastImport.rowsProcessed} nuevas, {lastImport.rowsDuplicated} actualizadas
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 px-3.5 py-2.5 rounded-md text-sm mb-4 font-medium flex items-center gap-2">
            <AlertTriangle className="size-4 shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 text-emerald-700 px-3.5 py-2.5 rounded-md text-sm mb-4 font-medium flex items-center gap-2">
            <CheckCircle2 className="size-4 shrink-0" /> {success}
          </div>
        )}

        <div className="pb-1">
          <div className="text-sm font-medium text-slate-900 mb-2">
            Meses cargados ({months.length})
          </div>
          {months.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No hay meses cargados. Sube un CSV de RingOver para empezar.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {months.map((m, idx) => {
                const total = Object.values(chunks[m].users).reduce((s, u) => s + (u.t || 0), 0);
                const userCount = Object.keys(chunks[m].users).length;
                return (
                  <div
                    key={m}
                    className={cn(
                      'flex justify-between items-center px-3.5 py-3',
                      idx < months.length - 1 && 'border-b border-slate-100'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="size-3.5 text-slate-400" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{m}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {fmtN(total)} llamadas · {userCount} usuarios
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => removeMonth(m)}
                      disabled={busy}
                      title={`Eliminar ${m}`}
                      aria-label={`Eliminar ${m}`}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function GestorRingOver() {
  const navigate = useNavigate();
  const { persona } = useContext(UsuarioContext);
  const esSuperAdmin = persona?.rol === 'Super Administrador';

  const [chunks, setChunks] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedUser, setSelectedUser] = useState('__ALL__');
  const [period, setPeriod] = useState({ preset: 'all', from: null, to: null });
  const [modalOpen, setModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [lastImport, setLastImport] = useState(null);

  const fetchChunks = useCallback(async (currentPeriod) => {
    try {
      setLoadError(null);
      const data = await fetchRingoverChunks(currentPeriod);
      setChunks(data || {});
    } catch (e) {
      setLoadError(e.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChunks(period); }, [fetchChunks, period]);

  // Carga inicial de la última sincronización (independiente del periodo)
  useEffect(() => {
    fetchLastImport().then(r => setLastImport(r.lastImport)).catch(() => {});
  }, []);

  // Disparar sincronización por API (solo Super Admin; el botón se renderiza condicionalmente)
  const handleSync = async () => {
    setSyncing(true); setSyncResult(null); setSyncError(null);
    try {
      const data = await syncRingoverAhora();
      setSyncResult(data);
      await fetchChunks(period);
      try { const r = await fetchLastImport(); setLastImport(r.lastImport); } catch {}
    } catch (error) {
      if (error?.response?.status === 409) {
        setSyncError({ type: 'locked', message: 'Ya hay una sincronización en curso. Espera a que termine antes de lanzar otra.' });
      } else {
        setSyncError({ type: 'error', message: 'No se pudo completar la sincronización. Revisa la conexión e inténtalo de nuevo.' });
      }
    } finally {
      setSyncing(false);
    }
  };

  const DATA = useMemo(() => aggregate(chunks), [chunks]);

  useEffect(() => {
    if (selectedUser !== '__ALL__' && !DATA.userList.includes(selectedUser)) setSelectedUser('__ALL__');
  }, [DATA.userList, selectedUser]);

  const stats = useMemo(() => {
    if (DATA.months.length === 0) return null;
    const source = selectedUser === '__ALL__' ? DATA.global : DATA.users[selectedUser];
    if (!source) return null;
    return source;
  }, [DATA, selectedUser]);

  const dailyChartData = useMemo(() => {
    if (!stats || !stats.byDay) return [];
    return Object.entries(stats.byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, d]) => {
        const [, mm, dd] = date.split('-');
        return { date: `${dd}/${mm}`, Contestadas: d.answered, Perdidas: d.missed };
      });
  }, [stats]);

  const hourChartData = useMemo(() => {
    if (!stats || !stats.byHour) return [];
    return Array.from({ length: 24 }, (_, h) => ({
      hour: `${String(h).padStart(2, '0')}h`,
      llamadas: stats.byHour[h] || 0,
    }));
  }, [stats]);

  const dowChartData = useMemo(() => {
    if (!stats || !stats.byDayOfWeek) return [];
    return DOW_LIST.map((d) => ({ day: d.slice(0, 3), llamadas: stats.byDayOfWeek[d] || 0 }));
  }, [stats]);

  const tagOverview = useMemo(() => {
    if (!stats) return [];
    return [
      { name: 'Con etiqueta', value: stats.conEtiqueta, color: CHART.brandLight },
      { name: 'Sin etiqueta', value: stats.sinEtiqueta, color: CHART.danger },
    ];
  }, [stats]);

  const monthlyChartData = useMemo(() => {
    const byMonth = (selectedUser === '__ALL__' ? DATA.global.byMonth : DATA.users[selectedUser]?.byMonth) || {};
    return DATA.months.map((m) => {
      const d = byMonth[m] || { conEtiqueta: 0, sinEtiqueta: 0 };
      return {
        mes: m.replace(/ 20(\d{2})$/, " '$1"),
        'Con etiqueta': d.conEtiqueta || 0,
        'Sin etiqueta': d.sinEtiqueta || 0,
      };
    });
  }, [DATA, selectedUser]);

  const topPerformers = useMemo(() => {
    if (selectedUser !== '__ALL__') return null;
    return DATA.userList.map((u) => {
      const us = DATA.users[u];
      if (!us || us.total === 0) return null;
      return {
        name: u, ...us,
        answerRate: us.total > 0 ? us.answered / us.total : 0,
        unlabeledRate: us.total > 0 ? us.sinEtiqueta / us.total : 0,
      };
    }).filter(Boolean).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [DATA, selectedUser]);

  const teamComparison = useMemo(() => {
    if (selectedUser === '__ALL__' || !stats) return null;
    const teamCount = DATA.userList.length;
    const globalRef = DATA.global;
    if (!globalRef || globalRef.total === 0) return null;
    return {
      avgTotal: Math.round(globalRef.total / teamCount),
      teamAnswerRate: globalRef.answered / globalRef.total,
      userAnswerRate: stats.total > 0 ? stats.answered / stats.total : 0,
      teamUnlabeledRate: globalRef.sinEtiqueta / globalRef.total,
      userUnlabeledRate: stats.total > 0 ? stats.sinEtiqueta / stats.total : 0,
    };
  }, [DATA, selectedUser, stats]);

  // % Conversión = Ventas Confirmadas VN / Total VN
  const conversionRate = useMemo(() => {
    if (!stats || stats.vnTotal === 0) return 0;
    return (stats.vnVentaConfirmada / stats.vnTotal) * 100;
  }, [stats]);

  const periodRange = useMemo(() => {
    if (DATA.months.length === 0) return '—';
    const allDates = Object.keys(DATA.global.byDay || {}).sort();
    if (!allDates.length) return DATA.months.join(' · ');
    return `${allDates[0]} a ${allDates[allDates.length - 1]}`;
  }, [DATA]);

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">
      <DataManagerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        chunks={chunks}
        lastImport={lastImport}
        onChange={fetchChunks}
      />

      <div>

        {/* ========== PAGE HEADER ========== */}
        <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-4 mb-5">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Estadísticas RingOver</h1>
            <p className="text-sm text-slate-500 mt-1">
              {selectedUser === '__ALL__'
                ? <>Análisis de llamadas · <span className="font-medium text-slate-700 tabular-nums">{stats ? fmtN(stats.total) : '—'}</span> llamadas · {periodRange}</>
                : <>{selectedUser} · <span className="font-medium text-slate-700 tabular-nums">{stats ? fmtN(stats.total) : '—'}</span> llamadas</>
              }
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/* Teleoperadora */}
            <div className="min-w-[220px]">
              <Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                <option value="__ALL__">Todas las teleoperadoras</option>
                {DATA.userList.map((u) => <option key={u} value={u}>{u}</option>)}
              </Select>
            </div>

            {/* Periodo */}
            <PeriodFilter value={period} onChange={setPeriod} />

            {/* Sincronizar ahora (solo Super Admin) */}
            {esSuperAdmin && (
              <Button onClick={handleSync} disabled={syncing}>
                <RefreshCw className={cn('size-3.5', syncing && 'animate-spin')} strokeWidth={2} />
                {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
              </Button>
            )}

            {/* Datos */}
            <Button variant="outline" onClick={() => setModalOpen(true)}>
              <Database className="size-3.5" strokeWidth={2} />
              Datos
              {DATA.months.length > 0 && (
                <span className="ml-1 px-1.5 py-px rounded-md bg-slate-100 text-[11px] font-semibold tabular-nums">
                  {DATA.months.length}
                </span>
              )}
            </Button>

            {/* Configuración (solo Super Admin) */}
            {esSuperAdmin && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate('/estadisticas/ringover/configuracion')}
                title="Configuración del dashboard (solo Super Admin)"
                aria-label="Configuración"
              >
                <Settings className="size-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Banner de feedback de la última sincronización */}
        {syncResult && (
          <div className="mt-3 flex items-center gap-2 px-3.5 py-2.5 rounded-md text-sm bg-green-50 text-green-700">
            <CheckCircle2 size={16} />
            <span>
              {syncResult.processed === 0
                ? 'Sincronización completada · sin llamadas nuevas'
                : `Sincronización completada · ${syncResult.processed} ${syncResult.processed === 1 ? 'llamada nueva' : 'llamadas nuevas'}`}
            </span>
          </div>
        )}
        {syncError && (
          <div className={cn(
            'mt-3 flex items-center gap-2 px-3.5 py-2.5 rounded-md text-sm',
            syncError.type === 'locked' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
          )}>
            {syncError.type === 'locked' ? <Clock size={16} /> : <AlertTriangle size={16} />}
            <span>{syncError.message}</span>
          </div>
        )}

        {/* ========== ESTADOS: LOADING / ERROR / EMPTY ========== */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Loader2 className="size-7 text-[hsl(var(--brand-light))] mx-auto animate-spin" />
              <div className="mt-3 text-sm">Cargando datos…</div>
            </CardContent>
          </Card>
        ) : loadError ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="size-8 text-red-500 mx-auto" />
              <div className="text-lg font-medium text-slate-900 mt-3.5">No se ha podido conectar con la API</div>
              <div className="text-sm text-slate-500 mt-1.5 mb-5">{loadError}</div>
              <Button onClick={() => fetchChunks(period)}>
                <RefreshCw className="size-3.5" /> Reintentar
              </Button>
            </CardContent>
          </Card>
        ) : DATA.months.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Database className="size-9 text-slate-300 mx-auto" strokeWidth={1.5} />
              <div className="text-xl font-medium text-slate-900 mt-4">No hay datos cargados</div>
              <div className="text-sm text-slate-500 mt-1.5 mb-5">
                Pulsa <strong className="font-medium">Sincronizar ahora</strong> para traer las llamadas desde RingOver.
              </div>
              <Button onClick={() => setModalOpen(true)}>Abrir gestor de datos</Button>
            </CardContent>
          </Card>
        ) : !stats ? (
          <Card>
            <CardContent className="py-10 text-center text-slate-500">
              Sin datos para los filtros seleccionados.
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ========== KPIs FILA 1 ========== */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <KpiCard
                icon={<Phone className="size-3.5" />}
                label="Total llamadas"
                value={fmtN(stats.total)}
                subtitle={getPeriodLabel(period)}
              />
              <KpiCard
                icon={<CheckCircle2 className="size-3.5" />}
                iconColor="success"
                label="Contestadas"
                value={fmtN(stats.answered)}
                subtitle={`${fmtPct(stats.answered, stats.total)} del total`}
              />
              <KpiCard
                icon={<Tag className="size-3.5" />}
                label="Etiquetadas"
                value={fmtN(stats.conEtiqueta)}
                subtitle={`${fmtPct(stats.conEtiqueta, stats.total)} del total`}
              />
              <KpiCard
                icon={<PhoneOutgoing className="size-3.5" />}
                label="Salientes"
                value={fmtN(stats.outbound)}
                subtitle={`${fmtPct(stats.outbound, stats.total)} del total`}
              />
            </div>

            {/* ========== KPIs FILA 2 ========== */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <KpiCard
                icon={<span className="w-3 h-3 rounded-full inline-block" style={{ background: CHART.brandLight }} />}
                label="Llamadas VN"
                value={fmtN(stats.vnTotal)}
                subtitle={`${fmtPct(stats.vnTotal, stats.total)} del total`}
              />
              <KpiCard
                icon={<span className="w-3 h-3 rounded-full inline-block" style={{ background: CHART.warning }} />}
                label="Llamadas RN"
                value={fmtN(stats.rnTotal)}
                subtitle={`${fmtPct(stats.rnTotal, stats.total)} del total`}
              />
              <KpiCard
                icon={<Clock className="size-3.5" />}
                label="Duración media"
                value={fmtDur(stats.avgDuration)}
                subtitle={`Total: ${fmtDur(stats.totalDuration)}`}
              />
              <KpiCard
                icon={<TrendingUp className="size-3.5" />}
                iconColor="success"
                label="% Conversión"
                value={`${conversionRate.toFixed(1)}%`}
                subtitle={`${fmtN(stats.vnVentaConfirmada)} ventas / ${fmtN(stats.vnTotal)} VN`}
              />
            </div>

            {/* ========== COMPARACIÓN CON EQUIPO (solo cuando hay usuario seleccionado) ========== */}
            {teamComparison && (
              <Card className="mb-3 border-l-2" style={{ borderLeftColor: 'hsl(var(--brand-light))' }}>
                <CardContent className="py-4 px-5 flex flex-wrap items-center justify-between gap-5">
                  <div>
                    <div className="text-[15px] font-medium text-slate-900">Comparación con la media del equipo</div>
                    <div className="text-xs text-slate-500 mt-0.5">Promedio por teleoperadora en el periodo</div>
                  </div>
                  <div className="flex flex-wrap gap-7">
                    <ComparisonStat
                      label="Llamadas"
                      value={fmtN(stats.total)}
                      delta={`${stats.total >= teamComparison.avgTotal ? '↑' : '↓'} vs media ${fmtN(teamComparison.avgTotal)}`}
                      positive={stats.total >= teamComparison.avgTotal}
                    />
                    <ComparisonStat
                      label="% Contestadas"
                      value={`${(teamComparison.userAnswerRate * 100).toFixed(1)}%`}
                      delta={`vs ${(teamComparison.teamAnswerRate * 100).toFixed(1)}%`}
                      positive={teamComparison.userAnswerRate >= teamComparison.teamAnswerRate}
                    />
                    <ComparisonStat
                      label="% Sin etiquetar"
                      value={`${(teamComparison.userUnlabeledRate * 100).toFixed(1)}%`}
                      delta={`vs ${(teamComparison.teamUnlabeledRate * 100).toFixed(1)}%`}
                      positive={teamComparison.userUnlabeledRate <= teamComparison.teamUnlabeledRate}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ========== CATEGORÍAS VN / RN ========== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
              <Card>
                <CardContent className="p-4">
                  <SectionHeader
                    title="Categorías VN"
                    subtitle="Distribución detallada de etiquetas Venta Nueva"
                    icon={Tag}
                  />
                  <div className="mt-3.5">
                    <TagBar label="VN — Visita confirmada" count={stats.vnVisitaConfirmada} total={stats.total} color={CHART.success} />
                    <TagBar label="VN — Venta confirmada" count={stats.vnVentaConfirmada} total={stats.total} color={CHART.success} />
                    <TagBar label="VN — Seguimiento" count={stats.vnSeguimiento} total={stats.total} color={CHART.brandLight} />
                    <TagBar label="VN — No localizable" count={stats.vnNoLocalizable} total={stats.total} color={CHART.warning} />
                    <TagBar label="VN — KO" count={stats.vnKo} total={stats.total} color={CHART.danger} sublabel="(incluye KO Definitivo y KOx2)" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <SectionHeader
                    title="Categorías RN"
                    subtitle="Distribución detallada de etiquetas Renovación"
                    icon={Tag}
                  />
                  <div className="mt-3.5">
                    <TagBar label="RN — Visita confirmada" count={stats.rnVisitaConfirmada} total={stats.total} color={CHART.success} />
                    <TagBar label="RN — Venta confirmada" count={stats.rnVentaConfirmada} total={stats.total} color={CHART.success} />
                    <TagBar label="RN — Seguimiento" count={stats.rnSeguimiento} total={stats.total} color={CHART.brandLight} />
                    <TagBar label="RN — No localizable" count={stats.rnNoLocalizable} total={stats.total} color={CHART.warning} />
                    <TagBar label="RN — KO" count={stats.rnKo} total={stats.total} color={CHART.danger} sublabel="(incluye KO Definitivo y KOx2)" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ========== DONUT + STACKED BAR ========== */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-3 mb-3">
              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Etiquetado de llamadas" subtitle="Cobertura del etiquetado" />
                  <div className="h-[200px] mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={tagOverview} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" stroke="none">
                          {tagOverview.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs mt-2">
                    {tagOverview.map((d) => (
                      <div key={d.name} className="flex justify-between items-center">
                        <span className="flex items-center gap-2 text-slate-700 font-medium">
                          <span className="size-2.5 rounded-sm" style={{ background: d.color }} />
                          {d.name}
                        </span>
                        <span className="text-slate-500">
                          <span className="font-medium text-slate-900 text-[13px] tabular-nums">{fmtN(d.value)}</span>
                          <span className="ml-1.5 text-[11px] tabular-nums">({fmtPct(d.value, stats.total)})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Evolución por mes" subtitle="Comparación mensual con / sin etiqueta" />
                  <div className="h-[260px] mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyChartData} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke={CHART.gridLine} vertical={false} />
                        <XAxis dataKey="mes" stroke={CHART.muted} fontSize={12} />
                        <YAxis stroke={CHART.muted} fontSize={11} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} iconType="square" />
                        <Bar dataKey="Con etiqueta" stackId="a" fill={CHART.brandLight} />
                        <Bar dataKey="Sin etiqueta" stackId="a" fill={CHART.danger} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ========== EVOLUCIÓN DIARIA ========== */}
            <Card className="mb-3">
              <CardContent className="p-4">
                <SectionHeader title="Evolución diaria" subtitle="Llamadas contestadas vs perdidas día a día" />
                <div className="h-[280px] mt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradAns" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.success} stopOpacity={0.25} />
                          <stop offset="100%" stopColor={CHART.success} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradMis" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.danger} stopOpacity={0.18} />
                          <stop offset="100%" stopColor={CHART.danger} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="2 4" stroke={CHART.gridLine} vertical={false} />
                      <XAxis dataKey="date" stroke={CHART.muted} fontSize={11} interval="preserveStartEnd" />
                      <YAxis stroke={CHART.muted} fontSize={11} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} iconType="circle" />
                      <Area type="monotone" dataKey="Contestadas" stroke={CHART.success} strokeWidth={2} fill="url(#gradAns)" />
                      <Area type="monotone" dataKey="Perdidas" stroke={CHART.danger} strokeWidth={2} fill="url(#gradMis)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* ========== POR HORA + POR DÍA ========== */}
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-3 mb-3">
              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Por hora del día" subtitle="Distribución horaria de llamadas" />
                  <div className="h-[220px] mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourChartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke={CHART.gridLine} vertical={false} />
                        <XAxis dataKey="hour" stroke={CHART.muted} fontSize={10} interval={1} />
                        <YAxis stroke={CHART.muted} fontSize={11} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="llamadas" fill={CHART.brandLight} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Por día" subtitle="Llamadas por día de la semana" />
                  <div className="h-[220px] mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dowChartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke={CHART.gridLine} vertical={false} />
                        <XAxis dataKey="day" stroke={CHART.muted} fontSize={11} />
                        <YAxis stroke={CHART.muted} fontSize={11} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="llamadas" fill={CHART.brandDark} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ========== RANKING ========== */}
            {topPerformers && topPerformers.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <SectionHeader
                    title="Ranking de teleoperadoras"
                    subtitle={`Las ${topPerformers.length} con más llamadas en el periodo · pulsa para ver detalle`}
                    icon={Award}
                  />
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full text-xs min-w-[700px]">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">#</th>
                          <th className="text-left py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Teleoperadora</th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Total</th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">% Cont.</th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">% Sin etiq.</th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">VN</th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">RN</th>
                          <th className="text-right py-2.5 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Dur. media</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topPerformers.map((p, idx) => (
                          <tr
                            key={p.name}
                            onClick={() => setSelectedUser(p.name)}
                            className={cn(
                              'cursor-pointer transition-colors hover:bg-slate-50',
                              idx < topPerformers.length - 1 && 'border-b border-slate-100'
                            )}
                          >
                            <td className={cn(
                              'py-3 pl-0 pr-2 text-base font-medium tabular-nums',
                              idx < 3 ? 'text-[hsl(var(--brand-light))]' : 'text-slate-400'
                            )}>
                              {idx + 1}
                            </td>
                            <td className="py-3 px-2 font-medium text-slate-900">{p.name}</td>
                            <td className="py-3 px-2 text-right text-[14px] font-medium text-slate-900 tabular-nums">{fmtN(p.total)}</td>
                            <td className="py-3 px-2 text-right text-emerald-600 font-medium tabular-nums">{(p.answerRate * 100).toFixed(0)}%</td>
                            <td className={cn(
                              'py-3 px-2 text-right font-medium tabular-nums',
                              p.unlabeledRate > 0.4 ? 'text-red-600' :
                              p.unlabeledRate > 0.2 ? 'text-amber-600' : 'text-emerald-600'
                            )}>
                              {(p.unlabeledRate * 100).toFixed(0)}%
                            </td>
                            <td className="py-3 px-2 text-right text-slate-500 tabular-nums">{fmtN(p.vnTotal)}</td>
                            <td className="py-3 px-2 text-right text-slate-500 tabular-nums">{fmtN(p.rnTotal)}</td>
                            <td className="py-3 pl-2 pr-0 text-right text-slate-900 tabular-nums">{fmtDur(p.avgDuration)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ========== FOOTER ========== */}
        <div className="mt-8 pt-5 border-t border-slate-200 flex flex-wrap justify-between items-center gap-3">
          <div className="text-[11px] text-slate-500">Datos de RingOver · Periodo {periodRange}</div>
          <div className="text-xs italic text-slate-500">
            {fmtN(DATA.global.total)} llamadas analizadas
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HELPER COMPONENT (interno)
// ============================================================
const ComparisonStat = ({ label, value, delta, positive }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className="flex items-baseline gap-2 mt-0.5">
      <span className="text-xl font-medium text-slate-900 tabular-nums">{value}</span>
      <span className={cn('text-[11px] font-medium tabular-nums', positive ? 'text-emerald-600' : 'text-red-600')}>
        {delta}
      </span>
    </div>
  </div>
);
